import { computed, onBeforeUnmount, onMounted, ref, toValue, unref, watch } from 'vue';
import { useQuery, useQueryClient } from '@tanstack/vue-query';

import { useEventHub } from '@satellite/composables/useEventHub';
import { yardQueryKeys } from './yard/yardQueryKeys';
import yardSpotAreaService from '@satellite/services/yardSpotArea-service';

import type { ComputedRef, Ref } from 'vue';
import type { Maybe } from '@nova/core';

export interface YardArea {
  id: string;
  name?: string;
  warehouseId?: string;
  orgId?: string;
}

export interface UseYardAreaPresenterV2Options {
  /**
   * Area (spot area) ID to fetch. Can be a ref for reactivity.
   * Optional if area (with id) is provided.
   */
  areaId?: Ref<Maybe<string>> | ComputedRef<Maybe<string>>;

  /**
   * Optional area object. Used as placeholderData while the query loads/refetches.
   * Effective id is derived from areaId or area.id (explicit areaId wins; mismatch triggers a warning).
   */
  area?: Ref<Maybe<YardArea>> | ComputedRef<Maybe<YardArea>> | YardArea;

  /**
   * If true, subscribes to websocket events and invalidates query on changes.
   * Default: true
   */
  live?: boolean;

  /**
   * Stale time in milliseconds.
   * Default: Infinity (never stale, until a Subspace event invalidates the query; manual refresh is always available)
   */
  staleTime?: number;

  /**
   * Callback for when area is updated.
   */
  onUpdateReceived?: (area: YardArea) => void;
}

export function useYardAreaPresenterV2(options: UseYardAreaPresenterV2Options) {
  const { areaId, area: areaOption, live = true, staleTime = Infinity, onUpdateReceived } = options;

  const eventHub = useEventHub();
  const queryClient = useQueryClient();

  const effectiveAreaId = computed((): string | null => {
    const explicitId = unref(areaId);
    const fromObject = toValue(areaOption)?.id ?? null;

    if (explicitId) {
      if (fromObject && fromObject !== explicitId) {
        console.warn(
          '[useYardAreaPresenterV2] areaId mismatch:',
          `explicit areaId (${explicitId}) does not match area.id (${fromObject}). Using explicit areaId.`
        );
      }
      return explicitId;
    }
    return fromObject;
  });

  const areaPlaceholder = computed(() => toValue(areaOption) ?? undefined);

  const isInViewport = ref(false);
  const pendingInvalidation = ref(false);

  const handleIntersect = (entry?: { isIntersecting?: boolean } | boolean) => {
    const isIntersecting = typeof entry === 'boolean' ? entry : entry?.isIntersecting ?? true;
    isInViewport.value = isIntersecting;

    if (isIntersecting && pendingInvalidation.value) {
      pendingInvalidation.value = false;
      invalidate();
    }
  };

  const areaQuery = useQuery({
    queryKey: computed(() =>
      effectiveAreaId.value ? yardQueryKeys.spotAreaDetail(effectiveAreaId.value) : []
    ),
    queryFn: async () => {
      const response = await yardSpotAreaService.getOneByIdV2(effectiveAreaId.value!, {
        enableCache: true
      });

      return response as YardArea;
    },
    enabled: computed(() => Boolean(effectiveAreaId.value) && isInViewport.value),
    placeholderData: areaPlaceholder,
    staleTime,
    refetchOnWindowFocus: false
  });

  const area = computed(() => areaQuery.data.value || null);
  const areaName = computed(() => area.value?.name ?? null);

  const isLoading = computed(() => areaQuery.isLoading.value);
  const isFetching = computed(() => areaQuery.isFetching.value);
  const error = computed(() => areaQuery.error.value);

  const invalidate = () => {
    if (effectiveAreaId.value) {
      queryClient.invalidateQueries({
        queryKey: yardQueryKeys.spotArea(effectiveAreaId.value)
      });
    }
  };

  const refresh = async () => {
    await areaQuery.refetch();
  };

  const handleUpdateSpotArea = (event: { id?: string }) => {
    if (event?.id === effectiveAreaId.value) {
      if (isInViewport.value) {
        invalidate();
      } else {
        pendingInvalidation.value = true;
      }
    }
  };

  watch(area, newArea => {
    if (onUpdateReceived && newArea) {
      onUpdateReceived(newArea);
    }
  });

  onMounted(() => {
    if (live) {
      eventHub.on('update-SpotArea', handleUpdateSpotArea);
    }
  });

  onBeforeUnmount(() => {
    if (live) {
      eventHub.off('update-SpotArea', handleUpdateSpotArea);
    }
  });

  return {
    area,
    areaName,

    isLoading,
    isFetching,
    error,

    refresh,
    invalidate,

    onIntersect: handleIntersect,

    query: areaQuery
  };
}
